import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import NewsListItem from "@/components/feature/NewsListItem";
import { newsList } from "@/mocks/news";
import { newsCategories } from "@/mocks/categories";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 5;

export default function News() {
  const { category = "" } = useParams();
  const [page, setPage] = useState(1);
  const meta = newsCategories.find((c) => c.key === category);

  useDocumentMeta({
    title: `${category || "影视资讯"} - 影视资讯 - 八戒影视`,
    description: `八戒影视${category || "影视资讯"}栏目，提供${category || "影视圈"}的最新资讯、深度影评与幕后故事，及时了解影视行业动态。`,
    keywords: `八戒影视,${category || "影视资讯"},影视新闻,影评,幕后故事`,
  });

  const list = useMemo(() => newsList.filter((n) => n.category === category), [category]);
  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = list.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视资讯", to: "/news/行业新闻" }, { label: category }]} />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-3">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{category}</h1>
            <p className="text-sm text-foreground-500 mt-2 max-w-2xl">
              {meta?.desc || "影视资讯与影评专题内容"}，为你梳理最新影视圈动态与值得关注的作品。
            </p>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
                {pageItems.map((item) => (
                  <NewsListItem key={item.id} item={item} />
                ))}
              </div>
              <Pagination current={currentPage} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <EmptyState
              title="该栏目暂时没有资讯"
              description="我们已经记录，会尽快补充相关内容。你可以先返回首页，浏览其他影视资讯。"
              actionLabel="返回首页"
              actionTo="/"
            />
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="text-base font-bold text-foreground-950 mb-4">资讯栏目</h2>
            <ul className="space-y-2">
              {newsCategories.map((c) => (
                <li key={c.key}>
                  <Link
                    to={`/news/${c.key}`}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-md text-sm transition-colors cursor-pointer ${
                      c.key === category
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    <i className={c.icon}></i>
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}